'use client';

import React, { createContext, useCallback, useRef, useMemo } from 'react';
import { VideoPlayerState, ReactPlayerInstance } from '../../types';
import { cleanupYouTubeIframe, cleanupTikTokIframe } from '../../utils';
import { isProdEnvironment } from 'utils/environment';

interface VideoPlayer {
  id: string;
  playInBackground: boolean;
  pause: () => void;
  playerRef: ReactPlayerInstance | null;
  playerState: VideoPlayerState;
}

type VideoPlayerContextType = {
  registerPlayer: (player: VideoPlayer) => void;
  unregisterPlayer: (id: string) => void;
  notifyPlayerStarted: (playerId: string, playInBackground: boolean) => void;
};

const defaultVideoPlayerContext: VideoPlayerContextType = {
  registerPlayer: () => {},
  unregisterPlayer: () => {},
  notifyPlayerStarted: () => {},
};

export const VideoPlayerContext = createContext<VideoPlayerContextType>(defaultVideoPlayerContext);

export default function VideoPlayerProvider({ children }: { children: React.ReactNode }) {
  const playersRef = useRef<Map<string, VideoPlayer>>(new Map());

  const registerPlayer = useCallback((player: VideoPlayer) => {
    playersRef.current.set(player.id, player);
  }, []);

  const unregisterPlayer = useCallback((id: string) => {
    const player = playersRef.current.get(id);
    if (player) {
      // Cleanup YouTube iframe if this is a YouTube player
      cleanupYouTubeIframe(player.playerRef);
      // Cleanup TikTok iframe if this is a TikTok player
      cleanupTikTokIframe(player.playerRef);
    }
    playersRef.current.delete(id);
  }, []);

  const pauseVideoPlayer = useCallback((player: VideoPlayer) => {
    if (!player.playerRef) return;

    try {
      // If available, ask the underlying YouTube player to pause directly
      const getInternalPlayer = player.playerRef.getInternalPlayer;
      if (typeof getInternalPlayer === 'function') {
        const youtubePlayer = getInternalPlayer('youtube');
        if (youtubePlayer && typeof (youtubePlayer as { pauseVideo?: unknown }).pauseVideo === 'function') {
          (youtubePlayer as { pauseVideo: () => void }).pauseVideo();
        }

        // If available, ask the underlying TikTok player to pause directly
        const tiktokPlayer = getInternalPlayer('tiktok');
        if (tiktokPlayer && typeof (tiktokPlayer as { pause?: unknown }).pause === 'function') {
          (tiktokPlayer as { pause: () => void }).pause();
        }
      }
    } catch (error) {
      if (!isProdEnvironment()) {
        // eslint-disable-next-line no-console
        console.error(`Error pausing player ${player.id}:`, error);
      }
    }
  }, []);

  const notifyPlayerStarted = useCallback(
    (playerId: string, playInBackground: boolean) => {
      // If this player has playInBackground: true, don't affect other players
      if (playInBackground) {
        return;
      }

      // Pause all other players that are currently playing, except those with playInBackground: true
      playersRef.current.forEach((player, id) => {
        if (id !== playerId && !player.playInBackground && player.playerState === VideoPlayerState.Playing) {
          // Call the player's pause method (which sets state to Paused)
          player.pause();
          // Also directly pause the video using the playerRef
          pauseVideoPlayer(player);
        }
      });
    },
    [pauseVideoPlayer],
  );

  // Memoize the context value to prevent unnecessary re-renders of consumers
  const contextValue = useMemo(
    () => ({
      registerPlayer,
      unregisterPlayer,
      notifyPlayerStarted,
    }),
    [registerPlayer, unregisterPlayer, notifyPlayerStarted],
  );

  return <VideoPlayerContext.Provider value={contextValue}>{children}</VideoPlayerContext.Provider>;
}
